import {
  formatQueryForJsonApi,
  formatRequestForJsonApi,
  User,
} from '@evolve/common-ui';
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import {
  formatQueryParams,
  JsonApiResponseDto,
  parseJsonApiResponse,
  prepareHeaders,
  Response,
  TQueryParams,
} from 'evolve-ui';
import { TOfferApplications } from './../../../offers-frontend/src/context/create-edit-offer.context';

export enum OfferStatus {
  PENDING = 1,
  VALIDATION = 2,
  ONLINE = 3,
  OFFLINE = 4,
}

export type TOffer = {
  id?: string;
  reference?: string;
  tasks?: string;
  tasksDescription?: string;
  profile?: string;
  categories?: string[];
  responsible: {
    name: string;
    email: string;
    acronym: string;
  };
  workSchedule?: string;
  conditions?: string;
  observations?: string;
  offerStatusId?: OfferStatus;
  startDate?: string;
  endDate?: string;
  location?: string;
  country?: string;
  district?: string;
  municipality?: string;
  /** MySQL FK from Mongo nested locality `id` (edit form select values). */
  countryId?: number;
  districtId?: number;
  municipalityId?: number;
  locationId?: number;
  agency?: {
    name: string;
    acronym: string;
    address: string;
    phone: string;
    location: string;
    postalCode: string;
  };
  contractType?: string;
  workRegime?: string;
  activitySector?: string;
  agencyId?: number;

  contacts?: {
    id: number;
    name: string;
  }[];
  officialProfessionalCategories?: {
    id: number;
    name: string;
  }[];
  createdUser?: User;
  created?: string;
  modified?: string;
  headerImgUrl?: string;
  headerImgMobileUrl?: string;
  imgUrl?: string;
  offerUrl?: string;

  shareUuid?: string;

  totalApplications?: number;
  registeredApplications?: number;
  candidateApplications?: number;
  otherApplications?: number;
  recentApplications?: number;

  websiteCategoryIds?: {
    _id: string;
    name: string;
    profession_area_id: {
      _id: string;
      name: string;
    };
  }[];
  failed?: any;
};

export type TOfferThumbnail = {
  activitySector: string;
  headerImgUrl: string;
  headerImgMobileUrl: string;
  imgUrl: string;
};

export const offersApi = createApi({
  reducerPath: 'offers',
  baseQuery: fetchBaseQuery({
    baseUrl: `${process.env.GATSBY_OFFERS_API_URL}`,
    mode: 'cors',
    prepareHeaders,
  }),
  tagTypes: ['Offer'],
  endpoints: (builder) => ({
    getOffers: builder.query<Response<TOffer>, TQueryParams>({
      providesTags: ['Offer'],
      query: formatQueryParams,
      transformResponse: (res, meta, args) => {
        return parseJsonApiResponse<TOffer>(
          res as JsonApiResponseDto<TOffer>,
          meta,
          args,
          true,
        );
      },
    }),
    getOffer: builder.query<Response<TOffer>, { offerId: string }>({
      providesTags: ['Offer'],
      query: ({ offerId }) => {
        return formatQueryForJsonApi({}, `${offerId}`);
      },
      transformResponse: parseJsonApiResponse<TOffer>,
    }),

    getOfferApplications: builder.query<
      Response<TOfferApplications>,
      TQueryParams & { offerId: string }
    >({
      providesTags: ['Offer'],
      query: ({ offerId, ...params }) =>
        formatQueryParams(params, `${offerId}/applications`),
      transformResponse: (res, meta, args) => {
        return parseJsonApiResponse<TOfferApplications>(
          res as JsonApiResponseDto<TOfferApplications>,
          meta,
          args,
          true,
        );
      },
    }),

    getNewOfferApplications: builder.query<
      Response<TOfferApplications>,
      TQueryParams & { offerId: string }
    >({
      providesTags: ['Offer'],
      query: ({ offerId, ...params }) => {
        return formatQueryParams(
          { populate: ['offer', 'employee', 'employee.jobPreferences'] },
          `${process.env.GATSBY_SITE_URL}/api/offers/portal/${offerId}/applications`,
        );
      },

      transformResponse: (res, meta, args) => {
        return parseJsonApiResponse<TOfferApplications>(
          res as JsonApiResponseDto<TOfferApplications>,
          meta,
          args,
          true,
        );
      },
    }),

    getSingleOffer: builder.query<Response<TOffer>, { offerId: string }>({
      providesTags: ['Offer'],
      query: ({ offerId }) => {
        return formatQueryForJsonApi({}, `${offerId}`);
      },
      transformResponse: parseJsonApiResponse<TOffer>,
    }),

    postOffer: builder.mutation<
      Response<TOffer>,
      TOffer & { sendToValidation: boolean }
    >({
      invalidatesTags: ['Offer'],
      query: (params) => {
        const { sendToValidation, ...body } = params;
        return {
          method: 'POST',
          url: sendToValidation ? '?sendToValidation=true' : '',
          body: {
            ...body,
          },
        };
      },
      transformResponse: parseJsonApiResponse<TOffer>,
    }),

    editOffer: builder.mutation<
      Response<TOffer>,
      Partial<TOffer> & { offerId: string }
    >({
      invalidatesTags: ['Offer'],
      query: (params) => {
        const { offerId, ...body } = params;
        return {
          method: 'PUT',
          url: `${offerId}`,
          body,
        };
      },
    }),

    deleteOffer: builder.mutation<Response<TOffer>, { offerId: string }>({
      invalidatesTags: ['Offer'],
      query: ({ offerId }) => {
        return formatRequestForJsonApi({}, 'DELETE', `${offerId}`);
      },
      transformResponse: parseJsonApiResponse<TOffer>,
    }),

    publishOffer: builder.mutation<Response<TOffer>, { offerId: string }>({
      invalidatesTags: ['Offer'],
      query: (params) => {
        const { offerId } = params;
        return {
          method: 'PATCH',
          url: `${offerId}/publish`,
        };
      },
    }),

    unpublishOffer: builder.mutation<Response<TOffer>, { offerId: string }>({
      invalidatesTags: ['Offer'],
      query: (params) => {
        const { offerId } = params;
        return {
          method: 'PATCH',
          url: `${offerId}/unpublish`,
        };
      },
    }),
    getActivitySectorThumbnails: builder.query<
      Response<TOfferThumbnail>,
      TQueryParams
    >({
      query: (params) => formatQueryParams(params, 'thumbnails'),
      transformResponse: (res, meta, args) => {
        return parseJsonApiResponse<TOfferThumbnail>(
          res as JsonApiResponseDto<TOfferThumbnail>,
          meta,
          args,
          true,
        );
      },
    }),
  }),
});

export const {
  useGetOffersQuery,
  useGetOfferApplicationsQuery,
  useGetNewOfferApplicationsQuery,
  useGetSingleOfferQuery,
  usePostOfferMutation,
  useDeleteOfferMutation,
  useEditOfferMutation,
  usePublishOfferMutation,
  useUnpublishOfferMutation,
  useGetOfferQuery,
  useGetActivitySectorThumbnailsQuery,
} = offersApi;
